// Freeman Identity Service - exchanges Okta access token for user details + roles

const IDENTITY_URL = (import.meta.env.VITE_IDENTITY_URL as string | undefined)
  ?? 'https://dev.api.freeman.com/identity/identity';

// Production uses a single Okta app (no clientKey needed); lower environments
// (dev/qa/uat) use multiple Okta apps and must send clientKey: "brg".
//
// NOTE: import.meta.env.PROD must NOT be used here — every environment image is
// built with `vite build` (mode=production), so PROD is `true` for qa/uat as
// well, which would wrongly drop the clientKey those environments require.
// Derive from the per-env identity URL instead: only production targets the
// apex api.freeman.com host (lower envs use {env}.api.freeman.com).
const IS_PROD_IDENTITY = /^https?:\/\/api\.freeman\.com(?:\/|$)/.test(IDENTITY_URL);
const CLIENT_KEY = IS_PROD_IDENTITY ? undefined : 'brg';

export interface IdentityUser {
  userId: string;
  name: string;
  email: string;
  roles: string[];
  enterpriseAccessToken?: string;
}

interface UserProfile {
  id?: string;
  userId?: string;
  email?: string;
  username?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  groups?: string[];
}

interface OktaLoginResponse {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  tokenType?: string;
  userProfile?: UserProfile;
  // Legacy flat structure support
  userId?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  roles?: string[];
  groups?: string[];
  permissions?: string[];
}

/**
 * Calls the Freeman Identity Service to exchange an Okta access token
 * for user details and BRG-specific roles.
 */
export async function getIdentityUser(oktaAccessToken: string): Promise<IdentityUser> {
  const response = await fetch(`${IDENTITY_URL}/okta-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      oktaAccessToken,
      ...(CLIENT_KEY && { clientKey: CLIENT_KEY }),
    }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    console.error('[Identity] Login failed:', response.status, errorText);
    throw new Error(`Identity service error: ${response.status}`);
  }

  const data: OktaLoginResponse = await response.json();
  // console.log('[Identity] Raw response:', JSON.stringify(data));
  // console.log('[Identity] Response keys:', Object.keys(data));
  // console.log('[Identity] userProfile:', JSON.stringify(data.userProfile));
  // console.log('[Identity] All role sources:', JSON.stringify({
  //   'userProfile.roles': data.userProfile?.roles,
  //   'userProfile.groups': data.userProfile?.groups,
  //   'data.roles': data.roles,
  //   'data.groups': data.groups,
  //   'data.permissions': data.permissions,
  // }));

  // Persist enterprise token for API authorization.
  // This token should be used for BRG API calls instead of the raw Okta access token.
  if (data.accessToken) {
    sessionStorage.setItem('tfc_access_token', data.accessToken);
    sessionStorage.setItem('tfc_token_issued_at', String(Date.now()));
    if (typeof data.expiresIn === 'number') {
      sessionStorage.setItem('tfc_token_expires_in', String(data.expiresIn));
    }
    if (data.refreshToken) {
      sessionStorage.setItem('tfc_refresh_token', data.refreshToken);
    }
  }

  // Normalize both nested (userProfile) and flat response structures.
  const profile: UserProfile = data.userProfile ?? {
    id: data.userId,
    userId: data.userId,
    name: data.name,
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    roles: data.roles,
    groups: data.groups,
  };
  // console.log('[Identity] Using profile:', JSON.stringify(profile));
  
  // Build display name from available fields
  const name = profile.name
    || profile.username
    || [profile.firstName, profile.lastName].filter(Boolean).join(' ')
    || profile.email
    || '';

  // Roles come from "roles", "groups", or "permissions" from the identity service
  const rawRoles = profile.roles ?? profile.groups ?? data.permissions ?? [];
  const roles = rawRoles.map((role: string) => {
    // Normalize Admin/Member naming: "BRG-Admin" → "BRG.Admin", "BRG-Member" → "BRG.Member"
    // Lead roles stay as-is: "BRG-Lead" remains "BRG-Lead"
    if (role === 'BRG-Admin' || role === 'BRG_Admin') return 'BRG.Admin';
    if (role === 'BRG-Member' || role === 'BRG_Member') return 'BRG.Member';
    return role;
  });
  //console.log('[Identity] Extracted roles:', roles, 'from raw:', rawRoles);

  const userId = profile.id ?? profile.userId ?? '';

  //console.log('[Identity] Parsed identity:', { userId, name, email: profile.email, roles });

  return {
    userId,
    name,
    email: profile.email ?? '',
    roles,
    enterpriseAccessToken: data.accessToken,
  };
}
